// Metas mês a mês (tabela `metas_mensais`, #6496). A aba Metas do Painel Executivo
// grava; o Analytics Comercial grava a meta do Comercial (com_kwh), a mesma linha.
// A tela inicial, os KPIs da aba CEO e a Performance Comercial só leem.
//
// A tabela é pequena (um punhado de linhas por indicador), então desce inteira.
// Enquanto a migration 20261028120000 não estiver aplicada, a leitura falha com
// "tabela não existe": as telas seguem com as metas de antes (validada ou
// sugerida) e a aba Metas avisa o que falta.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../../../src/lib/supabase';
import { inicioDoMes, type MetaMensal } from '../../../../../src/lib/dashboard/metasMensais';

const faltaTabela = (e: any) =>
  e?.code === '42P01' || e?.code === 'PGRST205'
  || (/metas_mensais/.test(String(e?.message ?? '')) && /exist|find/i.test(String(e?.message ?? '')));

export interface EstadoMetasMensais {
  metas: MetaMensal[];
  carregando: boolean;
  /** A migration ainda não foi aplicada no banco. */
  semTabela: boolean;
  erro: string | null;
  /** Define (ou troca) a meta do indicador a partir do mês. */
  salvar: (indicador: string, mes: string, meta: number, observacao?: string | null) => Promise<void>;
  /** Tira a meta definida NAQUELE mês: ele volta a repetir a anterior. */
  apagar: (indicador: string, mes: string) => Promise<void>;
}

export function useMetasMensais(): EstadoMetasMensais {
  const [metas, setMetas] = useState<MetaMensal[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [semTabela, setSemTabela] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const { data, error } = await supabase.from('metas_mensais').select('*').order('mes');
    if (error) {
      if (faltaTabela(error)) setSemTabela(true);
      else setErro(error.message || 'Não foi possível carregar as metas.');
      setMetas([]);
      setCarregando(false);
      return;
    }
    setMetas((data || []).map((l: any) => ({ ...l, meta: Number(l.meta), mes: String(l.mes).slice(0, 10) })) as MetaMensal[]);
    setSemTabela(false);
    setErro(null);
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = useCallback(async (indicador: string, mes: string, meta: number, observacao?: string | null) => {
    const { error } = await supabase.from('metas_mensais').upsert(
      { indicador, mes: inicioDoMes(mes), meta, observacao: observacao?.trim() || null },
      { onConflict: 'indicador,mes' },
    );
    if (error) throw new Error(error.message || 'Não foi possível salvar a meta.');
    await carregar();
  }, [carregar]);

  const apagar = useCallback(async (indicador: string, mes: string) => {
    const { error } = await supabase.from('metas_mensais').delete().eq('indicador', indicador).eq('mes', inicioDoMes(mes));
    if (error) throw new Error(error.message || 'Não foi possível tirar a meta.');
    await carregar();
  }, [carregar]);

  return { metas, carregando, semTabela, erro, salvar, apagar };
}
